import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip,
} from 'recharts';
import {
  Table as TableIcon, BarChart3, Download, CalendarClock, Save, Loader2,
  List, RotateCcw, Repeat, Clock, Store, TrendingDown, Trophy, CalendarDays,
  Filter, LayoutGrid, AlarmClock,
} from 'lucide-react';
import { PageHeader, Segmented } from '../components/ds';

const ICONS = {
  'bar-chart-3': BarChart3, 'calendar-days': CalendarDays, 'list': List, 'trophy': Trophy,
  'trending-down': TrendingDown, 'store': Store, 'alarm-clock': AlarmClock, 'filter': Filter,
  'rotate-ccw': RotateCcw, 'repeat': Repeat, 'clock': Clock, 'layout-grid': LayoutGrid,
};

const ORANGE = '#F26A1B';

const fmtEuro = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v) + ' €';
};
const fmtNum = (v, d = 0) => {
  if (v === null || v === undefined || v === '') return '—';
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: d }).format(v);
};
const fmtCell = (v, type) => {
  if (v === null || v === undefined || v === '') return '—';
  if (type === 'date') { const s = String(v).slice(0, 10); const [y, m, d] = s.split('-'); return d ? `${d}/${m}/${y}` : s; }
  if (type === 'euro') return fmtEuro(v);
  if (type === 'number') return fmtNum(v, 1);
  if (type === 'percent') return fmtNum(v, 1) + ' %';
  return String(v);
};

export default function RapportsPage() {
  const { api } = useAuth();
  const [catalogue, setCatalogue] = useState({ catalogue: [], columns: {}, group_keys: {}, measures: {} });
  const [source, setSource] = useState('simulations');
  const [ref, setRef] = useState({});
  const [activeReport, setActiveReport] = useState(null);
  const [reportTitle, setReportTitle] = useState('Rapport libre');

  // Générateur
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selStatut, setSelStatut] = useState([]);
  const [selType, setSelType] = useState([]);
  const [selEnseigne, setSelEnseigne] = useState([]);
  const [groupBy, setGroupBy] = useState('commercial');
  const [measure, setMeasure] = useState('count');
  const [extraFilters, setExtraFilters] = useState({});

  const [view, setView] = useState('table');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Charge catalogue une fois
  useEffect(() => {
    api().get('/rapports/catalogue').then(r => setCatalogue(r.data)).catch(() => {});
  }, [api]);

  // Charge référentiels quand la source change
  useEffect(() => {
    api().get(`/rapports/referentiels?source=${source}`).then(r => setRef(r.data)).catch(() => {});
    // reset filtres sur changement de source
    setSelStatut([]); setSelType([]); setSelEnseigne([]); setExtraFilters({});
    setGroupBy(source === 'simulations' ? 'commercial' : 'region');
    setMeasure('count');
    setActiveReport(null);
    setReportTitle('Rapport libre');
  }, [source, api]);

  const buildFilters = useCallback(() => {
    const f = { ...extraFilters };
    if (dateFrom) f.date_from = dateFrom;
    if (dateTo) f.date_to = dateTo;
    if (selStatut.length) f.statut = selStatut;
    if (selType.length) f.type = selType;
    if (selEnseigne.length) f.enseigne = selEnseigne;
    return f;
  }, [dateFrom, dateTo, selStatut, selType, selEnseigne, extraFilters]);

  const runQuery = useCallback(async (overrideView) => {
    setLoading(true);
    try {
      const body = { source, filters: buildFilters(), group_by: groupBy, measure, view: overrideView || view };
      const r = await api().post('/rapports/query', body);
      setResult(r.data);
    } catch (e) { toast.error('Erreur lors du chargement du rapport'); }
    finally { setLoading(false); }
  }, [source, buildFilters, groupBy, measure, view, api]);

  // Recharge auto quand les paramètres changent
  useEffect(() => {
    const t = setTimeout(() => { runQuery(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, groupBy, measure, dateFrom, dateTo, selStatut, selType, selEnseigne, extraFilters]);

  const applyPreset = (rep) => {
    const cfg = rep.config || {};
    setActiveReport(rep.id);
    setReportTitle(rep.label);
    setGroupBy(cfg.group_by || 'commercial');
    setMeasure(cfg.measure || 'count');
    setView(cfg.view || 'table');
    const pf = cfg.filters || {};
    setSelStatut(pf.statut || []);
    setSelType(pf.type || []);
    setSelEnseigne([]);
    const ef = {};
    if (pf.dormant_days) ef.dormant_days = pf.dormant_days;
    if (pf.no_visit_days) ef.no_visit_days = pf.no_visit_days;
    setExtraFilters(ef);
  };

  const doExport = async (format) => {
    setExporting(true);
    try {
      const body = { source, filters: buildFilters(), group_by: groupBy, measure, format, title: reportTitle };
      const res = await api().post('/rapports/export', body, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportTitle.replace(/\s+/g, '_')}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) { toast.error("Échec de l'export"); }
    finally { setExporting(false); }
  };

  const presets = useMemo(() => (catalogue.catalogue || []).filter(c => c.source === source), [catalogue, source]);
  const groupOptions = catalogue.group_keys?.[source] || [];
  const measureOptions = Object.entries(catalogue.measures || {});
  const statutRef = ref.statut || [];
  const typeRef = ref.type || [];
  const enseigneRef = ref.enseigne || [];

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const GROUP_LABELS = {
    commercial: 'Commercial', enseigne: 'Enseigne', statut: 'Statut', region: 'Région',
    type_pdv: 'Type de PDV', mois_ouverture: "Mois d'ouverture", type: 'Type', manager: 'Manager',
    secteur: 'Secteur', mois_signature: 'Mois de signature',
  };

  const chartData = result?.chart?.items || [];

  return (
    <AppLayout>
      <div className="max-w-[1280px] space-y-6" data-testid="rapports-page">
        {/* En-tête §5.6.1 */}
        <PageHeader overline="Pilotage · Extraction et analyse des données" title="RAPPORTS">
          <div data-testid="rapports-source-toggle">
            <Segmented
              value={source}
              onChange={setSource}
              options={[{ value: 'simulations', label: 'Simulations CAPEX' }, { value: 'comptes', label: 'Parc comptes' }]}
            />
          </div>
        </PageHeader>

        {/* Bibliothèque §5.6.2 — cartes blanches */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-label mb-3">Prêts à l'emploi</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {presets.map((rep) => {
              const Icon = ICONS[rep.icon] || List;
              const active = activeReport === rep.id;
              return (
                <button key={rep.id} onClick={() => applyPreset(rep)}
                  className={`text-left rounded-card bg-surface p-4 transition-all hover:shadow-[0_0_0_2px_#25272B] ${active ? 'shadow-[0_0_0_2px_#F26A1B]' : ''}`}
                  data-testid={`rapports-preset-${rep.id}`}>
                  <span className="inline-grid place-items-center w-10 h-10 rounded-ctrl mb-3 bg-bg text-graphite-900">
                    <Icon className="w-5 h-5" strokeWidth={1.9} />
                  </span>
                  <p className="font-semibold text-[15px] text-ink leading-snug">{rep.label}</p>
                  <p className="text-text-3 text-[13px] mt-1 leading-snug">{rep.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Rapport libre §5.6.3 */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-label mb-3">Rapport libre</p>
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            {/* GÉNÉRATEUR — bloc scène graphite */}
            <div className="rounded-scene bg-graphite-900 p-6 space-y-5 h-fit" data-testid="rapports-generateur">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-orange">Générateur</p>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-ondark-3">Source</label>
                <select value={source} onChange={(e) => setSource(e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-ctrl bg-graphite-800 border border-graphite-500 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange"
                  data-testid="rapports-gen-source">
                  <option value="simulations">Simulations CAPEX</option>
                  <option value="comptes">Parc comptes</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-ondark-3">Période (entre)</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full h-11 rounded-ctrl bg-graphite-800 border border-graphite-500 px-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-orange" data-testid="rapports-date-from" />
                  <span className="text-ondark-3">→</span>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="w-full h-11 rounded-ctrl bg-graphite-800 border border-graphite-500 px-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-orange" data-testid="rapports-date-to" />
                </div>
              </div>

              {source === 'simulations' && statutRef.length > 0 && (
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-ondark-3">Statut</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {statutRef.map((s) => (
                      <button key={s} onClick={() => toggle(selStatut, setSelStatut, s)}
                        className={`px-2.5 py-1 rounded-ctrl text-xs font-semibold transition-colors ${selStatut.includes(s) ? 'bg-orange text-orange-on' : 'border border-graphite-500 text-ondark-2 hover:border-graphite-400'}`}
                        data-testid={`rapports-statut-${s}`}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {source === 'comptes' && typeRef.length > 0 && (
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-ondark-3">Type</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {typeRef.map((s) => (
                      <button key={s} onClick={() => toggle(selType, setSelType, s)}
                        className={`px-2.5 py-1 rounded-ctrl text-xs font-semibold transition-colors ${selType.includes(s) ? 'bg-orange text-orange-on' : 'border border-graphite-500 text-ondark-2 hover:border-graphite-400'}`}
                        data-testid={`rapports-type-${s}`}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {enseigneRef.length > 0 && (
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-ondark-3">Enseigne</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {enseigneRef.slice(0, 30).map((s) => (
                      <button key={s} onClick={() => toggle(selEnseigne, setSelEnseigne, s)}
                        className={`px-2.5 py-1 rounded-ctrl text-xs font-semibold transition-colors ${selEnseigne.includes(s) ? 'bg-orange text-orange-on' : 'border border-graphite-500 text-ondark-2 hover:border-graphite-400'}`}
                        data-testid={`rapports-enseigne-${s}`}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-ondark-3">Regrouper par</label>
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-ctrl bg-graphite-800 border border-graphite-500 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange"
                  data-testid="rapports-group-by">
                  {groupOptions.map((g) => <option key={g} value={g}>{GROUP_LABELS[g] || g}</option>)}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-ondark-3">Mesure</label>
                <select value={measure} onChange={(e) => setMeasure(e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-ctrl bg-graphite-800 border border-graphite-500 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange"
                  data-testid="rapports-measure">
                  {measureOptions.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </div>

              <button onClick={() => runQuery()} disabled={loading}
                className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-ctrl text-sm font-bold bg-orange text-orange-on transition-colors hover:brightness-105 disabled:opacity-60"
                data-testid="rapports-generate-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Générer le rapport
              </button>
            </div>

            {/* APERÇU — carte blanche */}
            <div className="rounded-card bg-surface p-5 sm:p-6" data-testid="rapports-result">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <p className="font-display font-semibold text-ink text-4xl leading-none tabular-nums">{result ? fmtNum(result.total_count) : '—'}</p>
                  <p className="text-text-3 text-[13px] mt-1.5">
                    {reportTitle} · {result ? 'résultat(s)' : '—'}{result?.truncated ? ' · aperçu limité à 1000, export complet' : ''} · regroupé par {GROUP_LABELS[groupBy] || groupBy}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex rounded-ctrl bg-segmented p-1">
                    <button onClick={() => setView('table')} className={`px-2.5 py-1.5 rounded-[7px] text-xs font-semibold inline-flex items-center gap-1 transition-colors ${view === 'table' ? 'bg-graphite-900 text-white' : 'text-text-2'}`} data-testid="rapports-view-table"><TableIcon className="w-3.5 h-3.5" /> Tableau</button>
                    <button onClick={() => setView('chart')} className={`px-2.5 py-1.5 rounded-[7px] text-xs font-semibold inline-flex items-center gap-1 transition-colors ${view === 'chart' ? 'bg-graphite-900 text-white' : 'text-text-2'}`} data-testid="rapports-view-chart"><BarChart3 className="w-3.5 h-3.5" /> Graphique</button>
                  </div>
                  <button onClick={() => doExport('csv')} disabled={exporting} className="h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-ink hover:bg-surface-2 inline-flex items-center gap-1 disabled:opacity-50" data-testid="rapports-export-csv"><Download className="w-3.5 h-3.5" /> CSV</button>
                  <button onClick={() => doExport('xlsx')} disabled={exporting} className="h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-ink hover:bg-surface-2 inline-flex items-center gap-1 disabled:opacity-50" data-testid="rapports-export-xlsx">{exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} XLSX</button>
                  <button onClick={() => toast.info('Planification par email — bientôt disponible (Phase 3)')} className="h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-text-2 hover:bg-surface-2 inline-flex items-center gap-1" data-testid="rapports-plan"><CalendarClock className="w-3.5 h-3.5" /> Planifier</button>
                  <button onClick={() => toast.info('Rapports enregistrés — bientôt disponible (Phase 3)')} className="h-9 px-3 rounded-ctrl text-xs font-semibold bg-surface border border-border text-text-2 hover:bg-surface-2 inline-flex items-center gap-1" data-testid="rapports-save"><Save className="w-3.5 h-3.5" /> Enregistrer</button>
                </div>
              </div>

              {/* KPI strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 mb-5">
                {(result?.kpis || []).map((k, i) => (
                  <div key={i} className="rounded-ctrl bg-bg px-4 py-3" data-testid={`rapports-kpi-${i}`}>
                    <p className="text-[11px] text-label font-bold uppercase tracking-[0.08em]">{k.label}</p>
                    <p className={`font-display font-semibold text-2xl mt-0.5 tabular-nums ${k.accent === 'orange' ? 'text-orange-text' : 'text-ink'}`}>
                      {k.format === 'euro' ? (k.value >= 1000000 ? (k.value / 1000000).toFixed(1).replace('.', ',') + ' M€' : fmtEuro(k.value)) : fmtNum(k.value)}
                    </p>
                  </div>
                ))}
              </div>

              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-orange" /></div>
              ) : view === 'chart' ? (
                <div className="h-[360px] w-full" data-testid="rapports-chart">
                  {chartData.length === 0 ? (
                    <p className="text-text-3 text-center py-16 text-sm">Aucune donnée pour ce regroupement.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 40 }}>
                        <XAxis dataKey="label" tick={{ fill: '#55534F', fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={60} />
                        <YAxis tick={{ fill: '#55534F', fontSize: 11 }} />
                        <RTooltip cursor={{ fill: '#25272B08' }} contentStyle={{ background: '#FFFFFF', border: '1px solid #D9D6CE', borderRadius: 10, color: '#141416' }}
                          formatter={(v) => [measure.startsWith('sum') ? fmtEuro(v) : measure.startsWith('taux') ? fmtNum(v, 1) + ' %' : fmtNum(v, 1), result?.chart?.measure_label]} />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                          {chartData.map((_, i) => <Cell key={i} fill={ORANGE} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              ) : (
                <div className="overflow-auto max-h-[460px] rounded-ctrl border border-divider" data-testid="rapports-table">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-surface-2">
                      <tr>
                        {(result?.columns || []).map((c) => (
                          <th key={c.key} className="text-left font-bold uppercase tracking-[0.08em] text-label px-3 py-2 whitespace-nowrap border-b border-[#E6E4DE]">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(result?.rows || []).map((row, ri) => (
                        <tr key={ri} className="hover:bg-surface-2">
                          {(result?.columns || []).map((c) => (
                            <td key={c.key} className="px-3 py-2 whitespace-nowrap text-text-2 border-b border-divider">{fmtCell(row[c.key], c.type)}</td>
                          ))}
                        </tr>
                      ))}
                      {(!result?.rows || result.rows.length === 0) && (
                        <tr><td className="px-3 py-8 text-center text-text-3" colSpan={result?.columns?.length || 1}>Aucun enregistrement.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
